import { ReactNode, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import { Button } from '@/components/button';
import { formatPhoneNumber } from '@/util/helpers';
import { GetPersonalInfoResponse, TwoFactorMethod } from '@/api/api.schemas';
import { useTfaSms as useDeleteTfaSms } from '@/api/delete-sms-tfa-option';
import { useTfaPreferred } from '@/api/set-preferred-tfa-method';
import { IconName, MaterialSymbolIcon } from '@/components/material-symbol-link';
import { SmsTfaPhoneDialog, SmsTfaPhoneDialogMode } from './sms-tfa-phone-dialog';
import styles from './two-factor-information.module.css';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Banner } from '../banner';

const translationBase = 'patientInformation.tfa';

interface TwoFactorInformationProps {
  patient: GetPersonalInfoResponse | undefined;
  isFetchingPatient: boolean;
  onUpdate: () => void;
}

export function TwoFactorInformation({
  patient,
  isFetchingPatient,
  onUpdate,
}: TwoFactorInformationProps) {
  const { t } = useTranslation();
  const [phoneDialogState, setPhoneDialogState] = useState<SmsTfaPhoneDialogMode | undefined>();
  const {
    mutate: deleteSmsTfa,
    isPending: isDeleting,
    error: deleteSmsTfaError,
  } = useDeleteTfaSms();
  const {
    mutate: setPreferredTfa,
    isPending: isSettingPreferred,
    error: setPreferredTfaError,
  } = useTfaPreferred();

  if (isFetchingPatient) {
    return <Loader type="small" />;
  }

  const emailMethod = patient?.availableTfaMethods?.find(
    (method) => method.method === TwoFactorMethod.Email,
  );
  const phoneMethod = patient?.availableTfaMethods?.find(
    (method) => method.method === TwoFactorMethod.Phone,
  );

  const preferredMethod = patient?.preferredTfaMethod ?? TwoFactorMethod.Email;
  const hasMultipleMethodsAvailable =
    patient?.availableTfaMethods?.length && patient.availableTfaMethods.length > 1;
  const isEmailPreferred = preferredMethod === TwoFactorMethod.Email;
  const isPhonePreferred = preferredMethod === TwoFactorMethod.Phone;

  const phoneNumbers = getUniqueProfilePhones(patient);

  const handleRemove = () => {
    deleteSmsTfa(undefined, {
      onSuccess: onUpdate,
    });
  };

  const handleSetPreferred = (method: TwoFactorMethod) => {
    setPreferredTfa(
      { data: { method } },
      {
        onSuccess: onUpdate,
      },
    );
  };

  return (
    <div className={styles.section}>
      <p className={clsx('body-1', 'bold')}>{t(`${translationBase}.title`)}</p>

      <div className={styles.tfaDetailsContainer}>
        <p className="semibold">{t(`${translationBase}.description`)}</p>
        <Banner type="info" label={t(`${translationBase}.info`)} />
        <TfaMethodRow
          icon="mail"
          title={t(`${translationBase}.viaEmail`)}
          isActive={isEmailPreferred}
          isSettingPreferred={isSettingPreferred}
          onMakeActive={
            hasMultipleMethodsAvailable
              ? () => handleSetPreferred(TwoFactorMethod.Email)
              : undefined
          }
          value={
            <div className={styles.tfaMethodValue}>
              <p className={clsx('body-3', styles.email)}>
                {emailMethod?.destination ?? t('patientInformation.na')}
              </p>
            </div>
          }
        />

        <TfaMethodRow
          icon="chat"
          title={t(`${translationBase}.viaSms`)}
          isActive={isPhonePreferred}
          isSettingPreferred={isSettingPreferred}
          onMakeActive={
            hasMultipleMethodsAvailable
              ? () => handleSetPreferred(TwoFactorMethod.Phone)
              : undefined
          }
          value={
            phoneMethod ? (
              <div className={clsx(styles.tfaMethodValue, styles.smsTfaMethodValue)}>
                <p className="body-3">{formatPhoneNumber(phoneMethod.destination)}</p>
                <Button
                  variant="link"
                  className={styles.smsTfaChangeButton}
                  size="S"
                  leftIcon="sync"
                  onClick={() => setPhoneDialogState('change')}
                >
                  {t(`${translationBase}.change`)}
                </Button>
              </div>
            ) : (
              <p className={clsx('body-3', styles.tfaMethodValue)}>
                {t(`${translationBase}.noPhone`)}
              </p>
            )
          }
          action={
            phoneMethod ? (
              <Button
                variant="outlined"
                size="S"
                loading={isDeleting}
                className={styles.removeBtn}
                onClick={handleRemove}
                leftIcon="close"
              >
                {t(`${translationBase}.remove`)}
              </Button>
            ) : (
              <Button variant="outlined" size="S" onClick={() => setPhoneDialogState('add')}>
                {t(`${translationBase}.add`)}
              </Button>
            )
          }
        />

        {(deleteSmsTfaError || setPreferredTfaError) && (
          <Banner type="error" label={t('common.errors.unknown')} />
        )}
      </div>

      {phoneDialogState && (
        <SmsTfaPhoneDialog
          isOpen
          mode={phoneDialogState}
          currentPhone={phoneMethod?.destination}
          phoneNumbers={phoneNumbers}
          onClose={() => setPhoneDialogState(undefined)}
          onSuccess={onUpdate}
        />
      )}
    </div>
  );
}

interface TfaMethodRowProps {
  icon: IconName;
  title: string;
  value: ReactNode;
  action?: ReactNode;
  isActive?: boolean;
  isSettingPreferred?: boolean;
  onMakeActive?: () => void;
}

function TfaMethodRow({
  icon,
  title,
  value,
  action,
  isActive,
  isSettingPreferred,
  onMakeActive,
}: TfaMethodRowProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');

  return (
    <div className={styles.tfaMethodRow}>
      <MaterialSymbolIcon name={icon} className={styles.tfaMethodIcon} />
      <div className={styles.tfaMethodInfo}>
        <p className="semibold">{title}</p>
        {value}
        {isMobile && isActive && <ActiveLabel />}
      </div>
      <div className={styles.tfaMethodActions}>
        {!isMobile && isActive && <ActiveLabel />}
        {!isActive && onMakeActive && (
          <Button variant="outlined" size="S" loading={isSettingPreferred} onClick={onMakeActive}>
            {t(`${translationBase}.makeActive`)}
          </Button>
        )}
        {action}
      </div>
    </div>
  );
}

function ActiveLabel() {
  const { t } = useTranslation();
  return (
    <span className={clsx('semibold', styles.activeLabel)}>
      <MaterialSymbolIcon name="check" /> {t(`${translationBase}.active`)}
    </span>
  );
}

function getUniqueProfilePhones(patient: GetPersonalInfoResponse | undefined) {
  if (!patient) {
    return [];
  }

  const phoneNumbers: string[] = [];

  if (patient.cellPhone) {
    phoneNumbers.push(patient.cellPhone);
  }

  if (patient.surrogate?.phone && !phoneNumbers.includes(patient.surrogate.phone)) {
    phoneNumbers.push(patient.surrogate.phone);
  }

  return phoneNumbers;
}
